import { useState, useEffect } from 'react';
import { adminApi, User } from '../../services/adminApi';

export default function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.listUsers();
      setUsers(data.users || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newEmail.trim()) return;

    try {
      setCreating(true);
      setError(null);
      const result = await adminApi.createUser(newEmail.trim().toLowerCase());
      setTempPassword(result.temporaryPassword);
      setNewEmail('');
      loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (email: string) => {
    if (!confirm(`Are you sure you want to delete ${email}?`)) return;

    try {
      setActionLoading(email);
      await adminApi.deleteUser(email);
      loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async (email: string) => {
    try {
      setActionLoading(email);
      const result = await adminApi.resetPassword(email);
      alert(`Password reset for ${email}\n\nTemporary password: ${result.temporaryPassword}\n\nUser must change password on next login.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h2>User Management</h2>
        <p className="admin-page-subtitle">Manage user accounts and access</p>
      </div>

      {error && (
        <div className="admin-error">
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <div className="admin-content-area">
        <div className="admin-toolbar">
          <button
            className="admin-btn admin-btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            + Add User
          </button>
          <button
            className="admin-btn admin-btn-secondary"
            onClick={loadUsers}
            disabled={loading}
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="admin-loading">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="admin-placeholder">
            <div className="admin-placeholder-icon">👥</div>
            <h3>No Users Found</h3>
            <p>Click "Add User" to create the first user account.</p>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.email}>
                    <td>{user.email}</td>
                    <td>
                      <span className={`status-badge status-${user.status.toLowerCase()}`}>
                        {user.status}
                      </span>
                    </td>
                    <td>{formatDate(user.created)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="admin-btn-sm"
                          onClick={() => handleResetPassword(user.email)}
                          disabled={actionLoading === user.email}
                        >
                          Reset Password
                        </button>
                        <button
                          className="admin-btn-sm admin-btn-danger"
                          onClick={() => handleDeleteUser(user.email)}
                          disabled={actionLoading === user.email}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="admin-modal-overlay" onClick={() => !creating && setShowCreateModal(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <h3>Add New User</h3>

            {tempPassword ? (
              <div className="temp-password-display">
                <p>User created successfully!</p>
                <div className="password-box">
                  <label>Temporary Password:</label>
                  <code>{tempPassword}</code>
                </div>
                <p className="note">The user must change this password on first login.</p>
                <button
                  className="admin-btn admin-btn-primary"
                  onClick={() => {
                    setTempPassword(null);
                    setShowCreateModal(false);
                  }}
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    className="admin-input"
                    placeholder="user@company.com"
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    disabled={creating}
                  />
                </div>

                <div className="modal-actions">
                  <button
                    className="admin-btn admin-btn-secondary"
                    onClick={() => setShowCreateModal(false)}
                    disabled={creating}
                  >
                    Cancel
                  </button>
                  <button
                    className="admin-btn admin-btn-primary"
                    onClick={handleCreateUser}
                    disabled={creating || !newEmail.trim()}
                  >
                    {creating ? 'Creating...' : 'Create User'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
